import axios from "axios";
import React from "react";
import { Link, useHistory } from "react-router-dom";
import { v4 } from "uuid";
import Button from "../../components/form/button";
import Field from "../../components/form/field";
import { Form } from "../../components/form/form";
import { StoreContext } from "../../store";
import "../terms/terms.css";
import { shouldPurgeForDeletionResponse } from "./accountDeletionPolicy";

const ACCOUNT_DELETION_TIMEOUT_MS =
  Number(process.env.REACT_APP_API_TIMEOUT_MS) || 120000;
const LOCAL_ACCOUNT_DATA_KEYS = Object.freeze([
  "dt.plaid.linkToken",
  "dt.plaid.itemId",
  "dt.plaid.linkTokenCreatedAt",
  "dt.broker.hiddenEntries.v1",
  "redirect",
]);

const clearLocalAccountData = () => {
  LOCAL_ACCOUNT_DATA_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage may be disabled; continue clearing the remaining keys.
    }
  });
};

const AccountDeletion = () => {
  const { replace } = useHistory();
  const { store: { isAuth }, setResponse } = React.useContext(StoreContext);
  const [loading, setLoading] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState("");
  const [idempotencyKey, setIdempotencyKey] = React.useState();
  const [message, setMessage] = React.useState("");

  const resetSubmission = () => {
    setIdempotencyKey(undefined);
  };

  const updateConfirmation = (value) => {
    setConfirmation(value);
    resetSubmission();
  };

  const submitDeletion = async ({ currentPassword, confirmation }) => {
    if (loading) {
      return;
    }
    if (!currentPassword) {
      setMessage("Current password is required.");
      return;
    }
    if (confirmation !== "DELETE") {
      setMessage("Type DELETE exactly to confirm account deletion.");
      return;
    }

    setMessage("");
    setLoading(true);
    const requestIdempotencyKey = idempotencyKey || v4();
    if (!idempotencyKey) {
      setIdempotencyKey(requestIdempotencyKey);
    }

    try {
      const response = await axios.request({
        method: "POST",
        url: "/api/account/deletion",
        data: { currentPassword, confirmation: "DELETE" },
        headers: {
          "Idempotency-Key": requestIdempotencyKey,
          "X-Request-Id": v4(),
        },
        timeout: ACCOUNT_DELETION_TIMEOUT_MS,
      });
      const responseData = response?.data || {};
      setResponse(responseData);

      if (shouldPurgeForDeletionResponse(response.status, responseData)) {
        setResponse({ isAuth: false });
        clearLocalAccountData();
        replace("/auth/login");
        return;
      }

      setMessage(
        responseData.message ||
        responseData.data?.message ||
        "The account deletion request could not be completed."
      );
    } catch (error) {
      const responseData = error?.response?.data || {
        success: false,
        message: error?.message || "The account deletion request failed.",
      };
      setResponse(responseData);

      if (shouldPurgeForDeletionResponse(error?.response?.status, responseData)) {
        setResponse({ isAuth: false });
        clearLocalAccountData();
        replace("/auth/login");
        return;
      }

      setMessage(
        responseData.message ||
        "The account deletion request could not be completed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bodySec aboutSec">
      <div className="container">
        <h2 className="text-center">
          <span>Account Deletion</span>
        </h2>
        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 termsofuseclass">
            <p className="text-justify">
              DeepTrading.ai users can request deletion of their DeepTrading.ai
              access and DeepTrading.ai-specific data.
            </p>
            {isAuth === true ? (
              <>
                <p className="text-justify">
                  Enter your current password and type DELETE exactly to request
                  permanent deletion of your DeepTrading.ai access and
                  DeepTrading.ai-specific data.
                </p>
                <Form className="AnimationForm" onSubmit={submitDeletion}>
                  <div className="signUpTabs">
                    <div className="row">
                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6">
                        <Field
                          type="password"
                          name="currentPassword"
                          label="Current Password"
                          required
                          disabled={loading}
                          autoComplete="current-password"
                          autoFocus
                          onChange={resetSubmission}
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6">
                        <Field
                          type="text"
                          name="confirmation"
                          label="Type DELETE to Confirm"
                          required
                          disabled={loading}
                          autoComplete="off"
                          onChange={updateConfirmation}
                        />
                      </div>
                      <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
                        <Button
                          loading={loading}
                          disabled={loading || confirmation !== "DELETE"}
                          right
                        >
                          Delete Account
                        </Button>
                      </div>
                      {message && (
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 text-danger">
                          {message}
                        </div>
                      )}
                    </div>
                  </div>
                </Form>
              </>
            ) : (
              <p className="text-justify">
                To request deletion, email{" "}
                <a href="mailto:info@deeptrading.ai">info@deeptrading.ai</a> or use
                the <Link to="/contact-us">Contact Us</Link> form. Please include
                the username and email address associated with the account and write
                "Delete my DeepTrading.ai account" in the request.
              </p>
            )}
            <p className="text-justify">
              When processed, this request permanently removes DeepTrading.ai
              access and DeepTrading.ai-specific data such as watchlists, saved
              strategies, and app preferences.
            </p>
            <p className="text-justify">
              Optional broker connections and portfolio data will be disconnected
              and deleted where applicable.
            </p>
            <p className="text-justify">
              A shared identity or data used by another service is not deleted by
              this DeepTrading.ai request.
            </p>
            <p className="text-justify">
              Billing, transaction, security, fraud-prevention, and
              legal/compliance records may be retained where required by law or
              legitimate business obligations.
            </p>
            <p className="text-justify">
              Deletion requests are typically processed within 30 days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletion;
